"use client";

import * as React from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { cn } from "@/lib/cn";
import { usePrefersReducedMotion } from "@/lib/use-reduced-motion";
import { showcaseScreens } from "@/data/system-screens";
import SystemWindow from "./SystemWindow";

/**
 * Destaque das telas do sistema: a janela entra em perspectiva e "se levanta" conforme a
 * rolagem; abaixo, abas (com barra de progresso e avanço automático) trocam a tela.
 */
export default function SystemShowcase() {
  const reduced = usePrefersReducedMotion();
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(wrapRef, { margin: "-15% 0px" });
  const [active, setActive] = React.useState(0);
  const [playing, setPlaying] = React.useState(true);
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const count = showcaseScreens.length;
  const auto = playing && !reduced;
  const running = auto && inView;

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start end", "center 55%"],
  });
  const rotateX = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [24, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [0.88, 1]);
  const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [50, 0]);
  const glow = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [0.15, 1]);

  const select = (i: number) => {
    setActive(i);
    setPlaying(false); // quem escolhe a aba quer ler: para o avanço automático
  };

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    let next = i;
    if (e.key === "ArrowRight") next = (i + 1) % count;
    else if (e.key === "ArrowLeft") next = (i - 1 + count) % count;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = count - 1;
    else return;
    e.preventDefault();
    select(next);
    tabRefs.current[next]?.focus();
  };

  const current = showcaseScreens[active];

  return (
    <div>
      <div ref={wrapRef} className="relative">
        {/* brilho atrás da janela */}
        <motion.div
          aria-hidden="true"
          style={{
            opacity: glow,
            background:
              "radial-gradient(closest-side, rgb(var(--brand) / 0.5), rgb(var(--violet) / 0.16) 62%, transparent)",
          }}
          className="pointer-events-none absolute inset-x-[4%] -top-12 h-[75%] blur-2xl"
        />

        <div style={{ perspective: 1400 }}>
          <motion.div
            id="system-panel"
            role="tabpanel"
            aria-labelledby={`system-tab-${current.id}`}
            style={{ rotateX, scale, y, transformOrigin: "50% 0%" }}
            className="relative will-change-transform"
          >
            <SystemWindow
              screens={showcaseScreens}
              active={active}
              sizes="(min-width: 1280px) 1180px, 96vw"
            />
            {!reduced && (
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                aria-label={
                  playing ? "Pausar a apresentação das telas" : "Retomar a apresentação das telas"
                }
                className="absolute bottom-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-line/20 bg-ink/70 text-fg backdrop-blur transition-colors hover:bg-ink/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric"
              >
                {playing ? (
                  <Pause className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Play className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            )}
          </motion.div>
        </div>
      </div>

      {/* abas */}
      <div className="mx-auto mt-10 max-w-full">
        <div
          role="tablist"
          aria-label="Telas do sistema"
          className="mx-auto flex w-fit max-w-full gap-1 overflow-x-auto rounded-full border border-line/15 bg-white/[0.04] p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {showcaseScreens.map((s, i) => {
            const isActive = i === active;
            return (
              <button
                key={s.id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                id={`system-tab-${s.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="system-panel"
                tabIndex={isActive ? 0 : -1}
                onClick={() => select(i)}
                onKeyDown={(e) => onKeyDown(e, i)}
                className={cn(
                  "relative shrink-0 overflow-hidden rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric sm:px-5",
                  isActive ? "bg-white/[0.09] text-fg" : "text-muted hover:text-fg"
                )}
              >
                {isActive && auto && (
                  <span
                    aria-hidden="true"
                    className="tab-progress absolute inset-0 origin-left bg-electric/25"
                    style={{ animationPlayState: running ? "running" : "paused" }}
                    onAnimationEnd={() => setActive((a) => (a + 1) % count)}
                  />
                )}
                <span className="relative">{s.label}</span>
              </button>
            );
          })}
        </div>

        <p
          aria-live="polite"
          className="mx-auto mt-5 min-h-[4.5rem] max-w-2xl text-balance text-center text-sm leading-relaxed text-muted sm:min-h-[3.5rem] sm:text-base"
        >
          {current.text}
        </p>
      </div>
    </div>
  );
}
